// =====================================================
// CompareBar — Sticky bottom bar showing selected scholarships
// Appears when 1+ scholarships are added to compare.
// Shows mini cards for each selection with remove buttons,
// a "Compare Now" CTA, and a clear all button.
// =====================================================

import { X, ArrowRightLeft, Trash2 } from 'lucide-react';
import { useCompare } from '@/contexts/CompareContext';
import { cn } from '@/lib/utils';

const CompareBar = () => {
  const {
    compareList,
    removeFromCompare,
    clearCompare,
    openDrawer,
    maxCompare,
  } = useCompare();

  if (compareList.length === 0) return null;

  const emptySlots = maxCompare - compareList.length;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 animate-fade-in">
      {/* Frosted glass background */}
      <div className="bg-ds-card/95 backdrop-blur-lg border-t border-ds-border shadow-[0_-4px_24px_rgba(0,0,0,0.3)]">
        <div className="container mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center gap-3">
            {/* Selected scholarships */}
            <div className="flex-1 flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
              {compareList.map(s => (
                <div
                  key={s.id}
                  className="flex-shrink-0 flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-lg bg-ds-bg border border-ds-border max-w-[200px]"
                >
                  <div
                    className="w-2 h-6 rounded-full flex-shrink-0"
                    style={{ background: s.heroGradient }}
                  />
                  <span className="text-xs text-ds-gray-1 font-medium truncate">
                    {s.name}
                  </span>
                  <button
                    onClick={() => removeFromCompare(s.id)}
                    className="flex-shrink-0 p-1 rounded hover:bg-ds-gray-4 text-ds-gray-3 hover:text-ds-gray-1 transition-colors"
                    aria-label={`Remove ${s.name} from comparison`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}

              {/* Empty slot indicators */}
              {Array.from({ length: emptySlots }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="flex-shrink-0 w-[140px] h-[34px] rounded-lg border border-dashed border-ds-gray-4 flex items-center justify-center"
                >
                  <span className="text-[10px] text-ds-gray-4">+ Add to compare</span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-ds-gray-3 hidden md:block">
                {compareList.length}/{maxCompare}
              </span>

              <button
                onClick={clearCompare}
                className="p-2 rounded-lg text-ds-gray-3 hover:text-ds-red hover:bg-ds-red/10 transition-colors"
                aria-label="Clear all"
                title="Clear all"
              >
                <Trash2 className="w-4 h-4" />
              </button>

              <button
                onClick={openDrawer}
                disabled={compareList.length < 2}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                  compareList.length >= 2
                    ? "bg-ds-red text-white hover:bg-ds-red-hover shadow-lg shadow-ds-red/20"
                    : "bg-ds-gray-4 text-ds-gray-3 cursor-not-allowed"
                )}
              >
                <ArrowRightLeft className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {compareList.length < 2 ? 'Select 2+' : 'Compare Now'}
                </span>
                <span className="sm:hidden">
                  {compareList.length < 2 ? '2+' : 'Compare'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareBar;
